import { useEffect, useState } from 'react';
import { Sun, Moon, Puzzle, ExternalLink, Chrome } from 'lucide-react';
import { useExtension } from '@/hooks/useExtension';

export function SettingsGeneralPage() {
  const [theme, setTheme] = useState<'hypema-dark' | 'hypema-light'>('hypema-light');
  const { connected } = useExtension();

  useEffect(() => {
    const saved = localStorage.getItem('theme') as 'hypema-dark' | 'hypema-light' | null;
    if (saved) setTheme(saved);
  }, []);

  function toggleTheme() {
    const next = theme === 'hypema-dark' ? 'hypema-light' : 'hypema-dark';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.setAttribute('data-theme', next);
  }

  return (
    <div className="space-y-8 max-w-2xl mx-auto w-full animate-fade-in">
      <div>
        <h1 className="text-xl font-bold tracking-tight">General</h1>
        <p className="text-sm text-base-content/40 mt-1">Application preferences and integrations.</p>
      </div>

      {/* Appearance */}
      <div className="space-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
          Appearance
        </h2>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              {theme === 'hypema-dark' ? <Moon size={16} /> : <Sun size={16} />}
              <span>{theme === 'hypema-dark' ? 'Dark' : 'Light'} mode</span>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={theme === 'hypema-light'}
              onChange={toggleTheme}
            />
          </div>
        </div>
      </div>

      {/* Chrome Extension */}
      <div className="space-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
          Chrome Extension
        </h2>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                connected ? 'bg-success/10' : 'bg-base-content/5'
              }`}>
                <Chrome size={20} className={connected ? 'text-success' : 'text-base-content/40'} />
              </div>
              <div>
                <p className="text-sm font-medium">HyPeMa for Gmail</p>
                <p className="text-xs text-base-content/40">
                  {connected
                    ? 'Extension connected and working'
                    : 'Not detected — install to create Gmail drafts directly'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {connected ? (
                <span className="flex items-center gap-1.5 text-xs text-success font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-success" />
                  Connected
                </span>
              ) : (
                <a
                  href="https://developer.chrome.com/docs/extensions/get-started/tutorial/hello-world#load-unpacked"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-primary gap-1"
                >
                  Install <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Integrations */}
      <div className="space-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
          Integrations
        </h2>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-base-content/5 flex items-center justify-center">
              <Puzzle size={20} className="text-base-content/40" />
            </div>
            <div>
              <p className="text-sm font-medium">API Access</p>
              <p className="text-xs text-base-content/40">
                API keys and developer integrations — coming soon
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
